import { useState } from 'react';

import { capitalize } from '@legacy/utils/string';
import { Button, Stack, Typography } from '@mui/material';
import cx from 'classnames';
import { exists } from 'i18next';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import styles from './NewSupplier.module.scss';
import {
  type CountrySelectOption,
  useGetCountries,
} from '@/api/REST/countries';
import {
  CurrencyCodeSelectOption,
  getCurrencyCodeSelectOptionById,
  getSearchCurrencyCodeSelectOptions,
} from '@/api/typesense/currencyCodes';
import { TypesenseCollection } from '@/api/typesense/types';
import { InputFieldSkeletonLoader } from '@/components/ui/InputFieldSkeletonLoader';
import {
  requiredSelectOption,
  optionalBasicPhoneNumber,
} from '@/forms/schemas/definitions';
import { isRequired } from '@/forms/schemas/utils';
import { withFieldGroup } from '@/forms/useAppForm';
import { useUserAccountHasFeature } from '@/hooks/useUserAccountHasFeature';

export type NewSupplierFormFields = {
  supplierName: string;
  supplierNumber?: string;
  vatNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country: CountrySelectOption;
  phoneNumber?: string;
  email?: string;
  currency: CurrencyCodeSelectOption;
};

export const newSupplierDefaultValues: NewSupplierFormFields = {
  supplierName: '',
  supplierNumber: '',
  country: null,
  currency: null,
  vatNumber: '',
  address: '',
  postalCode: '',
  city: '',
  state: '',
  phoneNumber: '',
  email: '',
};

export const newSupplierSchema = z.object({
  supplierName: z.string().trim().min(1, 'validationErrors.Required'),
  supplierNumber: z.string().optional(),
  country: requiredSelectOption(),
  currency: requiredSelectOption({
    isoCode: z.string(),
  }),
  vatNumber: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  phoneNumber: optionalBasicPhoneNumber(),
  email: z
    .string()
    .trim()
    .transform((val) => (val.length === 0 ? undefined : val))
    .pipe(z.email().optional()),
});

export const newSupplierOnChangeSchema = z.object({
  ...newSupplierSchema.shape,
  email: z.string().optional(),
});

export type NewSupplierOutput = z.infer<typeof newSupplierSchema>;

interface NewSupplierFormProps extends Record<string, unknown> {
  isParentLoading?: boolean;
}

export const NewSupplierForm = withFieldGroup({
  defaultValues: newSupplierDefaultValues,
  props: {
    isParentLoading: false,
  } as NewSupplierFormProps,
  render: function Render({ group, isParentLoading }) {
    const { t } = useTranslation(['forms', 'expenses']);
    const [showOptionalFields, setShowOptionalFields] = useState(false);

    const { data: countriesData, isError: isCountriesError } =
      useGetCountries(); // loading we get from isParentLoading

    const {
      hasFeature: hasAutoSupplierNumberFeature,
      isLoading: isAutoSupplierNumberFeatureLoading,
    } = useUserAccountHasFeature('customerAutoNoIsActive');
    const {
      hasFeature: hasSupplierNumberAllowEmpty,
      isLoading: isSupplierNumberAllowEmptyLoading,
    } = useUserAccountHasFeature('customerNoAllowEmpty');

    // Get all field names from the schema
    const detailSchemaFields = Object.entries(newSupplierSchema.shape)
      .filter(([key, value]) => {
        // Show only optional fields that are not already shown in the form
        return !isRequired(value) && key !== 'supplierNumber';
      })
      .map(([key]) => key) as Array<keyof NewSupplierFormFields>;

    const conditionalSupplierNumberSchema = hasAutoSupplierNumberFeature
      ? undefined // field is not shown, so no need to validate
      : hasSupplierNumberAllowEmpty
        ? z.string().optional()
        : z.string().min(1, 'validationErrors.Required');

    const isLoading =
      isParentLoading ||
      isAutoSupplierNumberFeatureLoading ||
      isSupplierNumberAllowEmptyLoading;

    return (
      <Stack>
        {isLoading ? (
          <Stack>
            {[1, 2, 3].map((_, index) => (
              <InputFieldSkeletonLoader key={index} />
            ))}
          </Stack>
        ) : (
          <Stack gap={3}>
            <Stack>
              <group.AppField
                name={'supplierName'}
                listeners={{
                  onBlur: ({ value }) => {
                    if (value) {
                      group.setFieldValue('supplierName', value.trim());
                    }
                  },
                }}
              >
                {(Field) => (
                  <Field.TextInputField
                    required={isRequired(newSupplierSchema.shape.supplierName)}
                    label={t('labels.SupplierName')}
                    type="text"
                  />
                )}
              </group.AppField>
              {!hasAutoSupplierNumberFeature &&
                conditionalSupplierNumberSchema && (
                  <group.AppField
                    name={'supplierNumber'}
                    validators={{
                      onChange: conditionalSupplierNumberSchema,
                    }}
                    listeners={{
                      onBlur: ({ value }) => {
                        if (value) {
                          group.setFieldValue('supplierNumber', value.trim());
                        }
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.TextInputField
                        required={isRequired(conditionalSupplierNumberSchema)}
                        label={t('labels.SupplierNumber')}
                        type="text"
                      />
                    )}
                  </group.AppField>
                )}
              <group.AppField name={'country'}>
                {(Field) => (
                  <Field.SelectInputField
                    required={isRequired(newSupplierSchema.shape.country)}
                    label={t('labels.Country')}
                    options={countriesData ?? []}
                    helperText={
                      isCountriesError && (
                        <Typography variant="label" color="error" role="alert">
                          {t('expenses:newSupplierModal.CountriesLoadingError')}
                        </Typography>
                      )
                    }
                  />
                )}
              </group.AppField>
              <group.AppField name={'currency'}>
                {(Field) => (
                  <Field.SelectInputField
                    required={isRequired(newSupplierSchema.shape.currency)}
                    label={t('labels.Currency')}
                    searchFunction={getSearchCurrencyCodeSelectOptions}
                    typeSenseCollection={TypesenseCollection.CURRENCY_CODES}
                    getOptionByIdFallbackFunction={
                      getCurrencyCodeSelectOptionById
                    }
                  />
                )}
              </group.AppField>
            </Stack>

            <div
              className={cx(styles.OptionalFieldsContainer, {
                [styles.OptionalFieldsContainerVisible]: showOptionalFields,
              })}
            >
              {showOptionalFields ? (
                <Stack className={styles.OptionalFieldsContainer}>
                  {detailSchemaFields.map((fieldName) => {
                    const required = isRequired(
                      newSupplierSchema.shape[fieldName],
                    );
                    const translationKey = capitalize(fieldName);
                    const label = exists(`labels.${translationKey}`, {
                      ns: 'forms',
                    })
                      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        t(`labels.${translationKey}` as any)
                      : translationKey;

                    if (fieldName === 'email') {
                      return (
                        <group.AppField
                          key={fieldName}
                          name={fieldName}
                          validators={{
                            onBlur: newSupplierSchema.shape[fieldName],
                          }}
                          listeners={{
                            onBlur: ({ value }) => {
                              if (value) {
                                group.setFieldValue('email', value.trim());
                              }
                            },
                          }}
                        >
                          {(Field) => (
                            <Field.TextInputField
                              required={required}
                              label={label}
                              type="email"
                              autoComplete="off" // no autocomplete, as user will likely never fill in own personal information here
                            />
                          )}
                        </group.AppField>
                      );
                    }

                    return (
                      <group.AppField
                        key={fieldName}
                        name={fieldName}
                        listeners={{
                          onBlur: ({ value }) => {
                            if (value && typeof value === 'string') {
                              group.setFieldValue(fieldName, value.trim());
                            }
                          },
                        }}
                      >
                        {(Field) => (
                          <Field.TextInputField
                            required={required}
                            label={label}
                            type="text"
                            autoComplete="off" // no autocomplete, as user will likely never fill in own personal information here
                          />
                        )}
                      </group.AppField>
                    );
                  })}{' '}
                </Stack>
              ) : (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setShowOptionalFields(true)}
                  disabled={isParentLoading}
                >
                  {t('expenses:newSupplierModal.AddDetails')}
                </Button>
              )}
            </div>
          </Stack>
        )}
      </Stack>
    );
  },
});
